import { useEffect, useState } from "react";
import { Sparkles, Calculator, MessageSquare, Check, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatMoney } from "@/lib/money";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function HustleProPage() {
  const { user } = useAuth();
  const cur = user?.preferred_currency || "GBP";
  const [tab, setTab] = useState("plan");

  return (
    <div className="mx-auto max-w-6xl px-6 py-10 md:px-10">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Hustle Pro</p>
      <h1 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl">Hustle tools</h1>

      <div className="mt-6 flex gap-2">
        {[{ k: "plan", l: "7-day plan" }, { k: "calc", l: "Pricing calculator" }, { k: "script", l: "Client script" }].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`hustle-tab-${t.k}`}
            className={`rounded-full px-4 py-1.5 text-sm transition-colors ${tab === t.k ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:bg-secondary/80"}`}>
            {t.l}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "plan" && <PlanTab cur={cur} />}
        {tab === "calc" && <CalcTab cur={cur} />}
        {tab === "script" && <ScriptTab />}
      </div>
    </div>
  );
}

function PlanTab({ cur }) {
  const [topic, setTopic] = useState("");
  const [budget, setBudget] = useState(100);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);

  const load = async () => { const { data } = await api.get("/hustle/plans"); setPlans(data); };
  useEffect(() => { load(); }, []);

  const generate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/hustle/plan", { skill_or_topic: topic, budget: Number(budget) });
      toast.success("Hustle plan generated!");
      setTopic("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); } finally { setLoading(false); }
  };

  const toggle = async (planId, taskId, done) => {
    await api.post(`/hustle/plans/${planId}/tasks/${taskId}`, { done });
    if (done) toast.success("Nice — +2 points + £0.05 cashback");
    load();
  };
  const remove = async (planId) => { await api.delete(`/hustle/plans/${planId}`); load(); };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <form onSubmit={generate} className="space-y-4 rounded-2xl border border-border bg-card p-6">
        <div className="space-y-1.5"><Label>Skill or topic</Label><Input value={topic} onChange={(e) => setTopic(e.target.value)} required placeholder="e.g. graphic design, freelance writing" className="h-11" data-testid="hustle-topic" /></div>
        <div className="space-y-1.5"><Label>Budget ({cur})</Label><Input type="number" min="0" value={budget} onChange={(e) => setBudget(e.target.value)} className="h-11" data-testid="hustle-budget" /></div>
        <Button type="submit" disabled={loading} className="h-11 w-full" data-testid="hustle-generate"><Sparkles className="mr-2 h-4 w-4" /> {loading ? "Generating…" : "Generate 7-day plan"}</Button>
      </form>

      <div className="space-y-4">
        {plans.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            No plans yet — generate your first one.
          </div>
        ) : plans.map((p) => (
          <div key={p.plan_id} className="rounded-2xl border border-border bg-card p-5" data-testid={`hustle-plan-${p.plan_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-display text-lg">{p.title}</h3>
                {p.summary && <p className="mt-1 text-xs text-muted-foreground">{p.summary}</p>}
              </div>
              <Button variant="ghost" size="icon" onClick={() => remove(p.plan_id)}><Trash2 className="h-4 w-4" /></Button>
            </div>
            <div className="mt-3 space-y-2">
              {(p.tasks || []).map((t) => (
                <button key={t.task_id} onClick={() => toggle(p.plan_id, t.task_id, !t.done)} data-testid={`task-${t.task_id}`}
                  className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left text-sm transition-colors ${t.done ? "border-primary bg-primary/5 line-through opacity-70" : "border-border bg-background hover:border-primary/40"}`}>
                  <span className={`mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full border ${t.done ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>
                    {t.done && <Check className="h-3 w-3" />}
                  </span>
                  <div className="flex-1">
                    <div className="font-medium">Day {t.day}: {t.task}</div>
                    {t.deliverable && <div className="mt-0.5 text-xs text-muted-foreground">→ {t.deliverable}</div>}
                  </div>
                  {t.est_earning_gbp && <span className="rounded-full bg-accent/10 px-2 py-0.5 text-xs text-accent">{formatMoney(t.est_earning_gbp, cur)}</span>}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CalcTab({ cur }) {
  const [form, setForm] = useState({ cost_of_goods: 0, hours: 1, hourly_rate: 25, overhead_pct: 10, margin_pct: 40 });
  const [result, setResult] = useState(null);
  const compute = async () => {
    const { data } = await api.post("/hustle/pricing-calc", form);
    setResult(data);
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="space-y-4 rounded-2xl border border-border bg-card p-6">
        <h2 className="font-display text-lg flex items-center gap-2"><Calculator className="h-5 w-5 text-primary" /> Inputs</h2>
        {[
          { k: "cost_of_goods", l: `Cost of goods (${cur})` },
          { k: "hours", l: "Hours of work" },
          { k: "hourly_rate", l: `Your hourly rate (${cur})` },
          { k: "overhead_pct", l: "Overhead %" },
          { k: "margin_pct", l: "Profit margin %" },
        ].map((f) => (
          <div key={f.k} className="space-y-1.5">
            <Label>{f.l}</Label>
            <Input type="number" min="0" value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: Number(e.target.value) })} className="h-11" data-testid={`calc-${f.k}`} />
          </div>
        ))}
        <Button onClick={compute} className="h-11 w-full" data-testid="calc-compute">Calculate</Button>
      </div>
      <div className="rounded-2xl border border-border bg-card p-6">
        <h2 className="font-display text-lg">Result</h2>
        {result ? (
          <div className="mt-4 space-y-2 text-sm">
            <Row l="Labour" v={formatMoney(result.labour, cur)} />
            <Row l="Overhead" v={formatMoney(result.overhead, cur)} />
            <Row l="Base cost" v={formatMoney(result.base_cost, cur)} />
            <div className="my-3 border-t border-border" />
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Suggested price</div>
            <div className="font-display text-4xl text-accent" data-testid="calc-suggested">{formatMoney(result.suggested_price, cur)}</div>
            <div className="text-xs text-muted-foreground">Profit margin: {formatMoney(result.margin_amount, cur)}</div>
          </div>
        ) : <p className="mt-4 text-sm text-muted-foreground">Enter your costs to see a suggested price.</p>}
      </div>
    </div>
  );
}

function Row({ l, v }) {
  return <div className="flex justify-between"><span className="text-muted-foreground">{l}</span><span>{v}</span></div>;
}

function ScriptTab() {
  const [form, setForm] = useState({ service: "", audience: "", channel: "whatsapp" });
  const [script, setScript] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/hustle/client-script", form);
      setScript(data.script);
    } catch (err) { toast.error("Failed"); } finally { setLoading(false); }
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <form onSubmit={generate} className="space-y-4 rounded-2xl border border-border bg-card p-6">
        <h2 className="font-display text-lg flex items-center gap-2"><MessageSquare className="h-5 w-5 text-primary" /> Outreach script</h2>
        <div className="space-y-1.5"><Label>Your service</Label><Input value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} required placeholder="e.g. logo design" className="h-11" data-testid="script-service" /></div>
        <div className="space-y-1.5"><Label>Target audience</Label><Input value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} required placeholder="e.g. small restaurants in London" className="h-11" data-testid="script-audience" /></div>
        <div className="space-y-1.5"><Label>Channel</Label>
          <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
            <SelectTrigger className="h-11" data-testid="script-channel"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="whatsapp">WhatsApp</SelectItem><SelectItem value="instagram_dm">Instagram DM</SelectItem><SelectItem value="email">Email</SelectItem></SelectContent>
          </Select>
        </div>
        <Button type="submit" disabled={loading} className="h-11 w-full" data-testid="script-generate">{loading ? "Generating…" : "Generate script"}</Button>
      </form>
      <div className="rounded-2xl border border-border bg-card p-6">
        <h2 className="font-display text-lg">Output</h2>
        {script ? (
          <>
            <Textarea readOnly value={script} className="mt-4 min-h-[280px] text-sm" data-testid="script-output" />
            <Button className="mt-3" variant="outline" onClick={async () => { await navigator.clipboard.writeText(script); toast.success("Copied"); }}>Copy</Button>
          </>
        ) : <p className="mt-4 text-sm text-muted-foreground">Your script will appear here.</p>}
      </div>
    </div>
  );
}
